'use client';

import { type RefObject, useEffect, useState } from 'react';
import { safeCall } from '@/app/utils/safeCall';
export interface InViewOptions {
  /**
   * (Optional) The root element that is used to calculate intersections
   */
  root?: HTMLElement | null;

  /**
   * (Optional) Margin around the root. A string of one to four values similar to the CSS margin property
   * @default '0px'
   */
  rootMargin?: string;

  /**
   * (Optional) Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed
   * @default 0
   */
  threshold?: number | number[];

  /**
   * (Optional) Whether the observer should only trigger once
   * @default true
   */
  once?: boolean;

  /**
   * (Optional) Set an initial value to return until the element has been measured
   * @default false
   */
  initialState?: boolean;
}

/**
 * Custom hook to track the visibility of an element using IntersectionObserver.
 * Supports an option to observe the element only once.
 *
 * @param {React.RefObject<HTMLElement>} ref - The target element reference.
 * @param {InViewOptions} options - The options for the observer.
 *
 * Read more: [Storybook docs: useInView](https://tis-web-story-book.netlify.app/?path=/docs/hooks-useinview--documentation)
 */
export const useInView = (ref: RefObject<HTMLElement | null>, options: InViewOptions = {}, onObserverInit?: () => void): boolean => {
  const {
    root = null,
    rootMargin = '0px 0px 0px 0px',
    threshold = 0,
    once = true,
    initialState = false
  } = options;
  const [isInView, setIsInView] = useState<boolean>(initialState);
  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef || once && isInView) return;
    const observer = new IntersectionObserver(([entry]) => {
      const isIntersecting = entry.isIntersecting;
      setIsInView(isIntersecting);
      if (isIntersecting && once) {
        observer.unobserve(entry.target);
      }
    }, {
      threshold,
      rootMargin,
      root
    });
    observer.observe(currentRef);
    safeCall(onObserverInit);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return isInView;
};